
import { getAllStates, getCitiesByState, getStateName } from "@/data/estados-cidades";
import { normalizeUrl } from "@/lib/utils";

// Cache for city data to improve performance
const cityDataCache = new Map();

// Helper function to find city data with caching
export const findCityData = (cidade: string) => {
  if (!cidade) {
    console.error("No cidade parameter provided to findCityData");
    return null;
  }

  // Check cache first
  if (cityDataCache.has(cidade)) {
    return cityDataCache.get(cidade);
  }

  try {
    const allStates = getAllStates();
    
    if (!allStates || !Array.isArray(allStates)) {
      console.error("getAllStates() returned invalid data");
      return null;
    }
    
    let foundCity = false;
    let foundState = "";
    let originalCityName = "";
    
    // Look for the city in all states
    for (const state of allStates) {
      const stateCities = getCitiesByState(state.sigla);
      
      if (!stateCities || !Array.isArray(stateCities)) {
        continue;
      }
      
      const matchedCity = stateCities.find(
        stateCity => normalizeUrl(stateCity) === cidade
      );
      
      if (matchedCity) {
        foundCity = true;
        foundState = state.sigla;
        originalCityName = matchedCity;
        break;
      }
    }
    
    if (!foundCity) {
      return null;
    }
    
    const estadoNome = getStateName(foundState);
    
    if (!estadoNome) {
      return null;
    }
    
    const result = {
      foundCity,
      foundState,
      originalCityName,
      estadoNome
    };
    
    // Cache the result
    cityDataCache.set(cidade, result);
    
    return result;
  } catch (error) {
    console.error("Error in findCityData:", error);
    return null;
  }
};

// Function to generate metadata for the city
export const generateCityMetadata = (cidade: string) => {
  if (!cidade) {
    return {
      title: "Cidade não encontrada | LocalUp",
      description: "A cidade procurada não foi encontrada em nossa base de dados."
    };
  }

  const cityData = findCityData(cidade);
  
  if (!cityData) {
    return {
      title: "Cidade não encontrada | LocalUp",
      description: "A cidade procurada não foi encontrada em nossa base de dados."
    };
  }
  
  const { originalCityName, estadoNome } = cityData;
  const canonicalUrl = `https://www.localup.com.br/cidade/local/${cidade}`;
  
  return {
    title: `SEO Local em ${originalCityName} - ${estadoNome} | LocalUp`,
    description: `Serviços de SEO Local em ${originalCityName}, ${estadoNome}. Aumente sua visibilidade no Google e atraia mais clientes locais em ${originalCityName} através de estratégias personalizadas de marketing digital.`,
    canonical: canonicalUrl,
    openGraph: {
      title: `SEO Local em ${originalCityName} - ${estadoNome} | LocalUp`,
      description: `Serviços de SEO Local em ${originalCityName}, ${estadoNome}. Aumente sua visibilidade no Google e atraia mais clientes da região com nossa consultoria especializada.`,
      url: canonicalUrl,
      type: 'website',
      locale: 'pt_BR',
    },
    twitter: {
      card: 'summary_large_image',
      title: `SEO Local em ${originalCityName} - ${estadoNome}`,
      description: `Estratégias de SEO Local para empresas em ${originalCityName}`,
    },
    alternates: {
      canonical: canonicalUrl,
    },
    robots: {
      index: true,
      follow: true,
    },
    metadataBase: new URL('https://www.localup.com.br'),
  };
};

// Function to generate FAQ items for the city
export const generateFaqItems = (cidadeFormatted: string, estadoNome: string) => {
  return [
    {
      question: `Como o SEO local pode ajudar meu negócio em ${cidadeFormatted}?`,
      answer: `O SEO local ajuda sua empresa a aparecer nos resultados de busca quando clientes em ${cidadeFormatted} procuram seus produtos ou serviços. Isso aumenta a visibilidade para pessoas próximas que têm maior probabilidade de visitar sua loja física ou contratar seus serviços.`
    },
    {
      question: `Quanto tempo leva para ver resultados de SEO local em ${cidadeFormatted}?`,
      answer: `Os primeiros resultados geralmente começam a aparecer entre 30 e 90 dias, dependendo da competitividade do seu setor em ${cidadeFormatted} e do estado atual do seu site. Resultados mais significativos normalmente são percebidos após 3 a 6 meses de trabalho contínuo.`
    },
    {
      question: `Por que meu negócio não aparece no Google Maps em ${cidadeFormatted}?`,
      answer: `Existem vários fatores que podem influenciar sua visibilidade no Google Maps em ${cidadeFormatted}, como a otimização incompleta do seu Google Meu Negócio, falta de consistência nas informações, poucas avaliações ou baixa atividade de postagens. Nossa estratégia de SEO local corrige esses problemas.`
    },
    {
      question: `Quais são os principais fatores de ranking para SEO local em ${cidadeFormatted}?`,
      answer: `Os principais fatores incluem a otimização do Google Meu Negócio, consistência das informações de nome, endereço e telefone (NAP) em toda a web, quantidade e qualidade das avaliações locais, sinais de citação local em ${cidadeFormatted} e ${estadoNome}, e conteúdo relevante para buscas locais.`
    },
    {
      question: `Como posso conseguir mais avaliações para meu negócio em ${cidadeFormatted}?`,
      answer: `Implementamos estratégias para aumentar suas avaliações, como envio de e-mails de acompanhamento após o serviço, criação de QR codes para facilitar avaliações, treinamento da sua equipe para solicitar avaliações de forma adequada e resposta apropriada às avaliações existentes para incentivar o engajamento contínuo.`
    }
  ];
};

// Helper function to get all city URLs for sitemap in batches
export const getAllCityUrls = async (batchSize = 100) => {
  try {
    const allStates = getAllStates();
    const cityUrls = [];
    
    for (const state of allStates) {
      const cities = getCitiesByState(state.sigla);
      
      for (let i = 0; i < cities.length; i += batchSize) {
        const batch = cities.slice(i, i + batchSize);
        
        // Process this batch
        const batchUrls = batch.map(city => ({
          url: `https://www.localup.com.br/cidade/local/${normalizeUrl(city)}`,
          lastmod: new Date().toISOString().split('T')[0],
          changefreq: "weekly",
          priority: "0.6"
        }));
        
        cityUrls.push(...batchUrls);
        
        // Small delay to avoid blocking the main thread
        if (i + batchSize < cities.length) {
          await new Promise(resolve => setTimeout(resolve, 0));
        }
      }
    }
    
    return cityUrls;
  } catch (error) {
    console.error("Error generating all city URLs:", error);
    return [];
  }
};
